




















































import { State } from "vuex-class";
import { toDisplayOptions } from "@/api/helpers";
import { DisplaySetting, DisplaySettingOption } from "@/store/schemas";
import { KeyAnyValue } from "@/store/types";
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import HelpWidget from "./HelpWidget.vue";
import { SiteState } from "@/store/types";
import { displaySvgPath, hasDisplaySVG, matchDisplaySVG } from "@/api/display-svgs";
import { notEmptyString } from "@/api/validators";

@Component({
  components: {
    HelpWidget,
  },
  filters: {},
})
export default class DisplayOptionSet extends Vue {
  @State("site") site: SiteState;
  @Prop({ default: "submission" }) section: string;
  @Prop({ default: () => [] }) settings: DisplaySetting[];
  @Prop({ default: '' }) refId: string;
  @Prop({ default: null }) displayOptions: any;

  values: KeyAnyValue[] = [];

  showBgChange = false;

  contrastingColor = '';

  timeout = 0;

  mounted() {
    this.sync();
  }

  isDropdown(setting: DisplaySetting) {
    return this.matchWidgetType(setting) === 'dropdown';
  }

  isRadios(setting: DisplaySetting) {
    return this.matchWidgetType(setting) === 'radios';
  }

  isRgbSet(setting: DisplaySetting) {
    return this.matchWidgetType(setting) === 'rgbset';
  }

  isCheckbox(setting: DisplaySetting) {
    return this.matchWidgetType(setting) === 'boolean';
  }

  matchWidgetType(setting: DisplaySetting) {
    switch (setting.type) {
      case 'multiple_choice':
        return setting.options.length > 2 && setting.options[0].name.length > 16 ? 'dropdown' : 'radios';
      default:
        return setting.type;
    }
  }

  toStyle(hexVal = '') {
    return {
      'background-color': hexVal
    }
  }

  toLabel(setting: DisplaySetting) {
    switch (setting.type) {
      case "boolean":
        return "";
      default:
        return setting.label;
    }
  }

  optClasses(setting: DisplaySetting, op: DisplaySettingOption) {
    const cls = [setting.type];
    if (setting.optionIsEnabled(op, this.settings, this.values)) {
      cls.push('available');
    } else {
      cls.push('inactive');
    }
    if (setting.isActive(op, setting, this.values)) {
      cls.push('active');
    }
    if (notEmptyString(op.mode)) {
      cls.push(op.mode);
    }
    return cls;
  }

  isValidContrastingValue(setting: DisplaySetting, refValue = null): boolean {
    let valid = false;
    const op = setting.options.find(op => op.key === refValue);
    const otherSetting = this.settings.find(s => s.key === setting.differRowKey);
    if (otherSetting && op instanceof Object) {
      const vl2 = this.values.find(v => v.key == setting.differRowKey);
      if (vl2 instanceof Object) {
        const op2 = otherSetting.options.find(op => op.key == vl2.value);
        if (op2 instanceof Object) {
          valid = op2.mode !== op.mode;
        }
      }
    }
    return valid;
  }

  isValidOption(setting: DisplaySetting): { valid: boolean; hasValue: boolean } {
    const vl = this.values.find(v => v.key == setting.key);
    let valid = false;
    const hasValue = vl instanceof Object;
    if (hasValue && setting instanceof Object) {
      if (setting.type === 'rgbset') {
        valid = this.isValidContrastingValue(setting, vl.value);
      } else if (['boolean'].includes(setting.type)) {
        valid = typeof vl.value === 'boolean';
      } else {
        valid = vl.value !== null;
      }
    }
    return { valid, hasValue };
  }

  hasInvalidOption(setting: DisplaySetting): boolean {
    const { valid, hasValue } = this.isValidOption(setting);
    return hasValue && !valid;
  }

  isValid(setting: DisplaySetting): boolean {
    const { valid } = this.isValidOption(setting);
    return valid;
  }

  fieldRowClasses(setting: DisplaySetting, si = 0) {
    const validClass = this.isValid(setting) ? 'valid' : 'invalid';
    const typeClass = [setting.type.replace(/_/g, '-'), 'row'].join('-');
    const cls = [typeClass, ['setting', si + 1].join('-'), validClass];
    if (setting.splitOptions) {
      cls.push('split');
    }
    return cls;
  }

  rgbSelected(key, setting: DisplaySetting) {
    const relKey = setting.differ.split('/').shift();
    const vlIndex = this.values.findIndex(v => v.key === relKey);
    this.showBgChange = false;
    if (this.timeout) {
      clearInterval(this.timeout);
    }
    if (vlIndex >= 0) {
      const vl = this.values[vlIndex];
      if (vl instanceof Object) {
        const relSetting = this.settings.find(s => s.key === relKey);
        if (relSetting instanceof Object) {
          const currOpt = setting.options.find(op => op.key === key);
          const relOpt = relSetting.options.find(op => op.key === vl.value);
          if (relOpt instanceof Object) {
            if (relOpt.mode === currOpt.mode) {
              const firstOpt = relSetting.options.find(op => op.mode !== currOpt.mode);
              this.values[vlIndex].value = firstOpt.key;
              if (setting.key.includes('text')) {
                this.showBgChange = true;
                this.contrastingColor = relOpt.mode === 'dark' ? 'light' : 'dark';
                this.timeout = setTimeout(() => {
                  this.showBgChange = false;
                }, 15000);
              }
            }
          }
        }
      }
    }
  }

  get sectionLabel() {
    switch (this.section) {
      case 'work':
        return 'Container';
      case 'submission':
        return 'Main';
      case 'mediaItem':
      case 'mediaitem':
      case 'media':
        return 'Media';
      default:
        return this.section;
    }
  }

  get legend() {
    return [this.sectionLabel, 'display options'].join(' ');
  }

  get bgChangeLabel() {
    return `Please select your choice of ${this.contrastingColor} background colour`;
  }

  mayShowBgChange(setting: DisplaySetting) {
    return setting.type === 'rgbset' && setting.key.includes('back') && this.showBgChange;
  }

  sync() {
    this.values = this.settings.map(setting => {
      const {key } = setting;
      return { key, value: setting.matchValue(this.displayOptions) };
    });
  }

  fullSvgPath(setting, index = 0): string {
    return displaySvgPath(this.matchSVG(setting, index)) + '?t=5';
  }

  matchSVG(setting: DisplaySetting, index = 0): string {
    return matchDisplaySVG(setting, this.values, index);
  }

  hasSVG(setting: DisplaySetting, index = 0): boolean {
    return hasDisplaySVG(setting, this.values, index);
  }

  update() {
    if (this.values instanceof Array && this.values.length > 0) {
      this.$emit('update', toDisplayOptions(this.values), this.refId);
      const hasInvalidSettings = this.settings.some(s => this.hasInvalidOption(s));
      this.$emit('validate', !hasInvalidSettings)
    }
  }

  /* toggle(opVal = '', setting: DisplaySetting) {
    const valIndex = this.values.findIndex(v => v.key === setting.key);
    if (valIndex >= 0) {
      const valRow = this.values[valIndex];
      if (valRow instanceof Object && opVal === valRow.value) {
        this.values[valIndex].value = '';
      }
    }
  } */

  resetRgb(setting: DisplaySetting) {
    const valIndex = this.values.findIndex(v => v.key === setting.key);
    if (valIndex >= 0) {
      const valRow = this.values[valIndex]
      if (valRow instanceof Object) {
        this.values[valIndex].value = this.matchDefValRgb(setting);
      }
    }
  }

  matchDefValRgb(setting: DisplaySetting) {
    let defVal = setting.defVal;
    let ok = false;
    const hasDefVal = notEmptyString(defVal);
    if (hasDefVal) {
      ok = this.isValidContrastingValue(setting, defVal);
    } 
    if (!ok) {
      const bestIndex = setting.options.findIndex(op => this.isValidContrastingValue(setting, op.key));
      const refIndex = bestIndex < 0 ? hasDefVal? -1 : 0 : bestIndex;
      defVal = refIndex >= 0 && refIndex < setting.options.length? setting.options[refIndex].key : defVal;
    }
    return defVal;
  }

  resetLabel(setting: DisplaySetting) {
    return ["Reset", setting.label.toLowerCase()].join(" ");
  }

  @Watch('values', {deep: true })
  changeValues() {
    this.update();
  }
}
